import { trpc } from "lib/trpc";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { ClientDocument } from "@compass/shared/types";

type AiFields = Pick<
    ClientDocument,
    "aiOptOut" | "aiDescriptionStatus" | "aiDescriptionShort" | "aiDescriptionLong"
>;

const POLL_MS = 3_000;
const POLL_CAP_MS = 2 * 60_000;

/**
 * PRTL-1497: turn a recording's AI transcript on or off after it was uploaded (the
 * recorder only reads its sparkle when Stop is pressed). Turning it on asks the
 * server to transcribe it now, then polls until the transcript is ready, so the
 * owner sees it without a reload. Owner-only on the server, so callers enable this
 * only for the author's own recordings.
 */
export function useAudioTranscript(file: ClientDocument | undefined, enabled: boolean) {
    const { enqueueSnackbar } = useSnackbar();
    const utils = trpc.useUtils();
    // What this session changed, laid over the file the parent passed in.
    const [override, setOverride] = useState<AiFields | null>(null);
    const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);
    const [timedOut, setTimedOut] = useState(false);
    const polling = enabled && pollStartedAt !== null;

    const status = trpc.userContent.aiStatus.useQuery(
        { fileId: file?.id ?? "" },
        { enabled: polling && !!file, refetchInterval: polling ? POLL_MS : false, gcTime: 0 },
    );

    useEffect(() => {
        if (!polling || !status.data) return;
        // A reading from before this run (e.g. the DONE of the transcript just
        // cleared) must not end the new run or show the old transcript.
        if (pollStartedAt !== null && status.dataUpdatedAt < pollStartedAt) return;
        setOverride(status.data);
        const s = status.data.aiDescriptionStatus;
        if (s === "DONE" || s === "ERROR") setPollStartedAt(null);
    }, [polling, pollStartedAt, status.data, status.dataUpdatedAt]);

    // A recording still being transcribed when the page loads: keep checking on it.
    const loadedPending = enabled && file?.aiDescriptionStatus === "PENDING";
    useEffect(() => {
        if (loadedPending) setPollStartedAt((started) => started ?? Date.now());
    }, [loadedPending]);

    useEffect(() => {
        if (pollStartedAt === null) return;
        const t = setTimeout(() => {
            setPollStartedAt(null);
            setTimedOut(true);
        }, POLL_CAP_MS);
        return () => clearTimeout(t);
    }, [pollStartedAt]);

    const trigger = trpc.userContent.triggerAI.useMutation();
    const clear = trpc.userContent.clearAI.useMutation();

    const view: AiFields = {
        aiOptOut: file?.aiOptOut,
        aiDescriptionStatus: file?.aiDescriptionStatus,
        aiDescriptionShort: file?.aiDescriptionShort,
        aiDescriptionLong: file?.aiDescriptionLong,
        ...override,
    };
    const on = view.aiOptOut === false;
    // Null right after triggering, until the server marks it PENDING.
    const transcribing = polling || (on && view.aiDescriptionStatus === "PENDING" && !timedOut);

    const setOn = async (next: boolean) => {
        if (!file) return;
        const before = override;
        setTimedOut(false);
        if (next) {
            setOverride({
                aiOptOut: false,
                aiDescriptionStatus: "PENDING",
                aiDescriptionShort: null,
                aiDescriptionLong: null,
            });
            await utils.userContent.aiStatus.reset({ fileId: file.id });
            try {
                await trigger.mutateAsync({ fileId: file.id });
                setPollStartedAt(Date.now());
            } catch (e) {
                setOverride(before);
                enqueueSnackbar(
                    `Couldn't start the transcript: ${e instanceof Error ? e.message : "try again"}`,
                    { variant: "error" },
                );
            }
        } else {
            setPollStartedAt(null);
            setOverride({
                aiOptOut: true,
                aiDescriptionStatus: null,
                aiDescriptionShort: null,
                aiDescriptionLong: null,
            });
            try {
                await clear.mutateAsync({ fileId: file.id });
            } catch (e) {
                setOverride(before);
                enqueueSnackbar(
                    `Couldn't turn off the transcript: ${e instanceof Error ? e.message : "try again"}`,
                    { variant: "error" },
                );
            }
        }
    };

    return {
        view,
        on,
        transcribing,
        failed: on && view.aiDescriptionStatus === "ERROR",
        timedOut,
        busy: trigger.isPending || clear.isPending,
        setOn,
    };
}
